import { AuthMethod, Credentials, AuthResult, Profile } from '../types';
import { ProfileManager } from './ProfileManager';
import { AuthTokenManager, AuthTokens } from './AuthTokenManager';

/**
 * Authentication service for handling third-party OAuth providers
 * and managing authentication state
 */
export class AuthenticationService {
  private profileManager: ProfileManager;
  private tokenManager: AuthTokenManager;
  private authState: Map<string, any> = new Map();

  constructor(profileManager: ProfileManager) {
    this.profileManager = profileManager;
    this.tokenManager = AuthTokenManager.getInstance();
  }

  /**
   * Initialize Google OAuth authentication using Google Identity Services
   */
  async initializeGoogleAuth(): Promise<void> {
    // Wait for Google Identity Services script to load
    let attempts = 0;
    while (!(window as any).google?.accounts?.id && attempts < 20) {
      await new Promise(resolve => setTimeout(resolve, 200));
      attempts++;
    }

    if (!(window as any).google?.accounts?.id) {
      console.warn('Google Identity Services failed to load');
    }

    this.authState.set('google', { initialized: true });
  }

  /**
   * Initialize Facebook OAuth authentication
   */
  async initializeFacebookAuth(): Promise<void> {
    try {
      console.log('Initializing Facebook OAuth...');
      
      // Simulate loading Facebook SDK
      await this.simulateSDKLoad('facebook');
      
      this.authState.set('facebook', { initialized: true });
    } catch (error) {
      throw new Error('Failed to initialize Facebook authentication');
    }
  }

  /**
   * Initialize Apple OAuth authentication
   */
  async initializeAppleAuth(): Promise<void> {
    try {
      console.log('Initializing Apple OAuth...');
      
      // Simulate loading Apple Sign-In SDK
      await this.simulateSDKLoad('apple');
      
      this.authState.set('apple', { initialized: true });
    } catch (error) {
      throw new Error('Failed to initialize Apple authentication');
    }
  }

  /**
   * Initialize Yahoo OAuth authentication
   */
  async initializeYahooAuth(): Promise<void> {
    try {
      console.log('Initializing Yahoo OAuth...');
      
      // Simulate loading Yahoo OAuth SDK
      await this.simulateSDKLoad('yahoo');
      
      this.authState.set('yahoo', { initialized: true });
    } catch (error) {
      throw new Error('Failed to initialize Yahoo authentication');
    }
  }

  /**
   * Authenticate with Google using Google Identity Services
   */
  async authenticateWithGoogle(): Promise<AuthResult> {
    try {
      if (!this.authState.get('google')?.initialized) {
        await this.initializeGoogleAuth();
      }

      // Use Google Identity Services to get credential
      const credential = await this.getGoogleCredential();

      // Decode the JWT to get user info
      const payload = this.decodeJwtPayload(credential);

      const credentials: Credentials = {
        token: credential,
        email: payload.email,
      };

      // Create/update profile with real Google data
      const result = await this.profileManager.authenticateUser(AuthMethod.GOOGLE, credentials);

      // Update profile name and picture from Google
      if (result.success && result.profile) {
        if (payload.name) result.profile.name = payload.name;
        if (payload.picture) result.profile.avatar = payload.picture;
        if (payload.email) result.profile.email = payload.email;

        // Persist the updated profile
        await this.profileManager.syncGameData(result.profile);

        const authTokens = this.tokenManager.generateTokens(AuthMethod.GOOGLE, result.profile.id);
        this.tokenManager.storeTokens(authTokens);
      }

      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Google authentication failed'
      };
    }
  }

  /**
   * Trigger Google One Tap or popup sign-in and return the credential JWT.
   */
  private getGoogleCredential(): Promise<string> {
    return new Promise((resolve, reject) => {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        reject(new Error('Google Client ID not configured'));
        return;
      }

      const google = (window as any).google;
      if (!google?.accounts?.id) {
        reject(new Error('Google Identity Services not loaded'));
        return;
      }

      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: any) => {
          // Clean up temporary sign-in button if it exists
          const tempDiv = document.getElementById('google-signin-temp');
          if (tempDiv) document.body.removeChild(tempDiv);

          if (response.credential) {
            resolve(response.credential);
          } else {
            reject(new Error('No credential received from Google'));
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Show the One Tap prompt
      google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback: render a sign-in button popup
          const buttonDiv = document.createElement('div');
          buttonDiv.id = 'google-signin-temp';
          buttonDiv.style.position = 'fixed';
          buttonDiv.style.top = '50%';
          buttonDiv.style.left = '50%';
          buttonDiv.style.transform = 'translate(-50%, -50%)';
          buttonDiv.style.zIndex = '99999';
          buttonDiv.style.background = 'white';
          buttonDiv.style.padding = '2rem';
          buttonDiv.style.borderRadius = '12px';
          buttonDiv.style.boxShadow = '0 4px 24px rgba(0,0,0,0.2)';
          document.body.appendChild(buttonDiv);

          google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            width: 280,
          });

          // Add a cancel button
          const cancelBtn = document.createElement('button');
          cancelBtn.textContent = 'Cancel';
          cancelBtn.style.display = 'block';
          cancelBtn.style.margin = '1rem auto 0';
          cancelBtn.style.padding = '0.5rem 1.5rem';
          cancelBtn.style.border = '1px solid #ddd';
          cancelBtn.style.borderRadius = '6px';
          cancelBtn.style.cursor = 'pointer';
          cancelBtn.onclick = () => {
            document.body.removeChild(buttonDiv);
            reject(new Error('Google sign-in cancelled'));
          };
          buttonDiv.appendChild(cancelBtn);
        }
      });
    });
  }

  /**
   * Decode a JWT payload without verification (client-side only).
   * The token is verified server-side in production.
   */
  private decodeJwtPayload(token: string): { email: string; name: string; picture: string; sub: string } {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return { email: '', name: '', picture: '', sub: '' };
    }
  }

  /**
   * Authenticate with Facebook
   */
  async authenticateWithFacebook(): Promise<AuthResult> {
    try {
      if (!this.authState.get('facebook')?.initialized) {
        await this.initializeFacebookAuth();
      }

      // Simulate Facebook OAuth flow
      const token = await this.simulateOAuthFlow('facebook');
      
      const credentials: Credentials = { token };
      const result = await this.profileManager.authenticateUser(AuthMethod.FACEBOOK, credentials);
      
      // Store tokens if authentication successful
      if (result.success && result.profile) {
        const authTokens = this.tokenManager.generateTokens(AuthMethod.FACEBOOK, result.profile.id);
        this.tokenManager.storeTokens(authTokens);
        
        return {
          ...result,
          token: authTokens.accessToken
        };
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Facebook authentication failed'
      };
    }
  }

  /**
   * Authenticate with Apple
   */
  async authenticateWithApple(): Promise<AuthResult> {
    try {
      if (!this.authState.get('apple')?.initialized) {
        await this.initializeAppleAuth();
      }

      // Simulate Apple OAuth flow
      const token = await this.simulateOAuthFlow('apple');
      
      const credentials: Credentials = { token };
      const result = await this.profileManager.authenticateUser(AuthMethod.APPLE, credentials);
      
      // Store tokens if authentication successful
      if (result.success && result.profile) {
        const authTokens = this.tokenManager.generateTokens(AuthMethod.APPLE, result.profile.id);
        this.tokenManager.storeTokens(authTokens);
        
        return {
          ...result,
          token: authTokens.accessToken
        };
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Apple authentication failed'
      };
    }
  }

  /**
   * Authenticate with Yahoo
   */
  async authenticateWithYahoo(): Promise<AuthResult> {
    try {
      if (!this.authState.get('yahoo')?.initialized) {
        await this.initializeYahooAuth();
      }

      // Simulate Yahoo OAuth flow
      const token = await this.simulateOAuthFlow('yahoo');
      
      const credentials: Credentials = { token };
      const result = await this.profileManager.authenticateUser(AuthMethod.YAHOO, credentials);
      
      // Store tokens if authentication successful
      if (result.success && result.profile) {
        const authTokens = this.tokenManager.generateTokens(AuthMethod.YAHOO, result.profile.id);
        this.tokenManager.storeTokens(authTokens);
        
        return {
          ...result,
          token: authTokens.accessToken
        };
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Yahoo authentication failed'
      };
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(authMethod: AuthMethod, currentToken: string): Promise<string> {
    try {
      console.log(`🔄 Refreshing token for ${authMethod}...`);
      
      // Use token manager to refresh
      const newTokens = await this.tokenManager.refreshAccessToken();
      
      if (!newTokens) {
        throw new Error('Failed to refresh token');
      }
      
      return newTokens.accessToken;
    } catch (error) {
      throw new Error(`Failed to refresh ${authMethod} token`);
    }
  }

  /**
   * Validate authentication token
   */
  async validateToken(authMethod: AuthMethod, token: string): Promise<boolean> {
    try {
      // In a real implementation, this would validate the token with the provider
      console.log(`Validating token for ${authMethod}...`);
      
      // Simulate token validation
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Basic token format validation
      const expectedPrefix = `${authMethod}_token_`;
      return token.startsWith(expectedPrefix) && token.length > expectedPrefix.length + 10;
    } catch (error) {
      return false;
    }
  }

  /**
   * Revoke authentication token
   */
  async revokeToken(authMethod: AuthMethod, token: string): Promise<void> {
    try {
      // In a real implementation, this would revoke the token with the provider
      console.log(`Revoking token for ${authMethod}...`);
      
      // Simulate token revocation
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Clear tokens from storage
      this.tokenManager.clearTokens();
      
      // Clear local auth state
      this.authState.delete(authMethod);
    } catch (error) {
      throw new Error(`Failed to revoke ${authMethod} token`);
    }
  }

  /**
   * Get available authentication methods
   */
  getAvailableAuthMethods(): AuthMethod[] {
    return [
      AuthMethod.GOOGLE,
      AuthMethod.FACEBOOK,
      AuthMethod.APPLE,
      AuthMethod.YAHOO,
      AuthMethod.CUSTOM,
      AuthMethod.GUEST
    ];
  }

  /**
   * Check if authentication method is initialized
   */
  isAuthMethodInitialized(authMethod: AuthMethod): boolean {
    switch (authMethod) {
      case AuthMethod.GOOGLE:
      case AuthMethod.FACEBOOK:
      case AuthMethod.APPLE:
      case AuthMethod.YAHOO:
        return this.authState.get(authMethod)?.initialized === true;
      case AuthMethod.CUSTOM:
      case AuthMethod.GUEST:
        return true; // These don't require initialization
      default:
        return false;
    }
  }

  /**
   * Initialize all third-party authentication providers
   */
  async initializeAllProviders(): Promise<void> {
    try {
      await Promise.all([
        this.initializeGoogleAuth(),
        this.initializeFacebookAuth(),
        this.initializeAppleAuth(),
        this.initializeYahooAuth()
      ]);
    } catch (error) {
      console.warn('Some authentication providers failed to initialize:', error);
      // Don't throw - partial initialization is acceptable
    }
  }

  /**
   * Get authentication state for debugging
   */
  getAuthState(): Record<string, any> {
    const state: Record<string, any> = {};
    this.authState.forEach((value, key) => {
      state[key] = value;
    });
    return state;
  }

  /**
   * Check if user has valid stored session
   */
  async hasValidSession(): Promise<boolean> {
    return await this.tokenManager.hasValidTokens();
  }

  /**
   * Attempt to restore session from stored tokens
   */
  async restoreSession(): Promise<AuthResult> {
    try {
      const tokens = await this.tokenManager.getTokens();
      
      if (!tokens) {
        return {
          success: false,
          error: 'No stored session found'
        };
      }

      const validation = await this.tokenManager.validateTokens();
      
      if (!validation.isValid) {
        // Try to refresh if expired
        if (validation.isExpired) {
          console.log('🔄 Session expired, attempting to refresh...');
          const refreshedTokens = await this.tokenManager.refreshAccessToken();
          
          if (!refreshedTokens) {
            this.tokenManager.clearTokens();
            return {
              success: false,
              error: 'Session expired and refresh failed'
            };
          }
        }
      }

      // Load profile for the authenticated user
      const profile = this.profileManager.getCurrentProfile();
      
      if (!profile) {
        this.tokenManager.clearTokens();
        return {
          success: false,
          error: 'Profile not found for stored session'
        };
      }

      console.log('✅ Session restored successfully', {
        authMethod: tokens.authMethod,
        userId: tokens.userId
      });

      return {
        success: true,
        profile,
        token: tokens.accessToken
      };
    } catch (error) {
      console.error('Failed to restore session:', error);
      this.tokenManager.clearTokens();
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Session restoration failed'
      };
    }
  }

  /**
   * Get session information
   */
  getSessionInfo() {
    return this.tokenManager.getSessionInfo();
  }

  /**
   * Schedule automatic token refresh
   */
  async scheduleTokenRefresh(callback: () => void): Promise<NodeJS.Timeout | null> {
    return await this.tokenManager.scheduleTokenRefresh(callback);
  }

  /**
   * Simulate SDK loading (for demonstration purposes)
   */
  private async simulateSDKLoad(provider: string): Promise<void> {
    // Simulate network delay for loading SDK
    await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
    
    // Simulate potential failure (5% chance)
    if (Math.random() < 0.05) {
      throw new Error(`Failed to load ${provider} SDK`);
    }
  }

  /**
   * Simulate OAuth flow (for demonstration purposes)
   */
  private async simulateOAuthFlow(provider: string): Promise<string> {
    // Simulate user interaction and OAuth flow
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));
    
    // Simulate potential user cancellation (10% chance)
    if (Math.random() < 0.1) {
      throw new Error('User cancelled authentication');
    }
    
    // Generate mock token
    return `${provider}_token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Authentication state manager for handling authentication events
 */
export class AuthStateManager {
  private listeners: Map<string, Function[]> = new Map();
  private currentUser: Profile | null = null;

  /**
   * Add event listener for authentication events
   */
  addEventListener(event: 'login' | 'logout' | 'tokenRefresh', callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  /**
   * Remove event listener
   */
  removeEventListener(event: 'login' | 'logout' | 'tokenRefresh', callback: Function): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  /**
   * Emit authentication event
   */
  private emit(event: string, data?: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} event listener:`, error);
        }
      });
    }
  }

  /**
   * Set current user and emit login event
   */
  setCurrentUser(user: Profile): void {
    this.currentUser = user;
    this.emit('login', user);
  }

  /**
   * Clear current user and emit logout event
   */
  clearCurrentUser(): void {
    const previousUser = this.currentUser;
    this.currentUser = null;
    this.emit('logout', previousUser);
  }

  /**
   * Get current user
   */
  getCurrentUser(): Profile | null {
    return this.currentUser;
  }

  /**
   * Emit token refresh event
   */
  notifyTokenRefresh(authMethod: AuthMethod, newToken: string): void {
    this.emit('tokenRefresh', { authMethod, newToken });
  }
}